import React, { FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { NO_DATA, ARROW_LEFT } from "../../../assets";
import TableComponent from "../../../shared/components/table";
import { CaseFolderColumns } from "../../../shared/components/table/columns/caseFolders";
import { SortingState } from "@tanstack/react-table";

import CaseFolderDetailsModal from "./internals/case-folder/modals/CaseFolderDetailsModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  JoinAsType,
  openCaseModal,
  selectJoinAs,
  setCaseDetailModal,
  setJoinAs,
} from "../../../states/slices/globalReducer";
import ModalV2 from "../../../shared/components/modalV2";

const partyType = [
  { value: "DEFENDANT", label: "Defendant Counsel" },
  { value: "THIRD_PARTY", label: "Third-Party Counsel" },
];
const CaseFolders: FC = () => {
  const [hasData, setHasData] = useState(false);
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const showCaseFolderDetails = useAppSelector(openCaseModal);
  const dispatch = useAppDispatch();
  const [joinCaseModal, setJoinCaseModal] = useState(false);
  const [appealNumberModal, setAppealNumberModal] = useState(false);
  const selectedJoinedAs = useAppSelector(selectJoinAs);

  const mockData = [
    {
      sn: false,
      date: "31-05-2024",
      title: "Olugbenga Akintola vs Israel Anyemalu",
      status: "Awaiting Submission",
      details: "View",
      query_status: "——",
      payment: "——",
      appeal_number: "——",
    },
    {
      sn: false,
      date: "31-05-2024",
      title: "Olugbenga Akintola vs Israel Anyemalu",
      status: "——",
      details: "View",
      query_status: "——",
      payment: "——",
      appeal_number: "——",
    },
    {
      sn: false,
      date: "31-05-2024",
      title: "Olugbenga Akintola vs Israel Anyemalu",
      status: "——",
      details: "View",
      query_status: "——",
      payment: "——",
      appeal_number: "——",
    },
  ];

  const handleCreateCase = () => {
    // For demo purposes, let's toggle data on create
    // setHasData(true);
    navigate("/police/case-folders/create");
  };

  return (
    <div className="flex flex-col h-full  min-h-screen">
      {/* Header */}
      <div className="bg-white px-8 py-6 border-b border-[#E9EAEB] flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">
          Case folder history
        </h1>
        <div className="flex items-center gap-x-4">
          <button
            onClick={() => setJoinCaseModal(true)}
            className="px-10 py-2 bg-[#00B074] text-white rounded-lg font-medium hover:bg-[#009b65] transition-colors"
          >
            Join a case
          </button>
          <button
            onClick={handleCreateCase}
            className="px-4 py-2 border border-[#E9EAEB] text-[#1A1A1A] rounded-lg font-medium flex items-center gap-x-2 hover:bg-gray-50 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create a new case</span>
          </button>
        </div>
      </div>

      {/* Breadcrumb & Search */}
      <div className="px-8 py-4 flex flex-col gap-y-6">
        <div className="flex items-center border-b border-[#E9EAEB] w-full gap-x-2 text-sm text-[#464255] pb-5">
          <Link to="/police/overview" className="hover:text-[#00B074]">
            Home
          </Link>
          <img src={ARROW_LEFT} alt="arrow" className="w-3 h-3" />
          <span className="font-medium text-[#1A1A1A]">
            Case folder history
          </span>
        </div>

        <div className="relative w-full max-w-[300px]">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#E9EAEB] focus:outline-none focus:border-[#00B074] bg-white text-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9A9A9A]" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-8 pb-8">
        {hasData ? (
          // Empty State
          <div className="flex flex-col items-center justify-center h-[60vh] gap-y-2">
            <img src={NO_DATA} alt="No Data" className="w-[200px] h-[200px]" />
            <span className="text-[#464255] font-medium text-lg">
              You have not created any case folder
            </span>
            <button
              onClick={handleCreateCase}
              className="px-6 py-3 border border-[#E9EAEB] text-[#1A1A1A] rounded-lg font-medium flex items-center gap-x-2 hover:bg-white bg-white shadow-sm transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>Create a new case</span>
            </button>
          </div>
        ) : (
          // List State
          <div className="bg-white rounded-xl ">
            <TableComponent
              DATA={mockData}
              // @ts-ignore
              COLUMNS={CaseFolderColumns}
              sorting={sorting}
              setSorting={setSorting}
            />

            {/* Pagination Mockup */}
            <div className="flex items-center justify-end gap-x-4 mt-6 text-sm text-[#464255]">
              <span>Page 1 of 10</span>
              <div className="flex items-center gap-x-2">
                <button className="px-3 py-1.5 border border-[#E9EAEB] rounded hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1.5 border border-[#E9EAEB] rounded hover:bg-gray-50">
                  Next
                </button>
              </div>
              <div className="flex items-center gap-x-2">
                <span>Entries per page:</span>
                <select className="border border-[#E9EAEB] rounded p-1 focus:outline-none">
                  <option>10</option>
                  <option>20</option>
                  <option>50</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <CaseFolderDetailsModal
        open={showCaseFolderDetails || false}
        close={() => dispatch(setCaseDetailModal(false))}
      />
      <ModalV2
        isOpen={appealNumberModal}
        isClose={() => setAppealNumberModal(false)}
        maxWidth="w-[442px]"
        edges="rounded-xl"
      >
        <div className="px-8 py-10 flex flex-col items-start w-full gap-y-5">
          <span className="text-2xl font-semibold">Enter Appeal number</span>
          <input
            type="text"
            className="w-full border rounded p-3 outline-none text-sm"
          />
          <button
            onClick={() => {
              setAppealNumberModal(false);
              navigate("/police/case-folders/search-result");
            }}
            className="bg-[#009444] w-full text-white px-10 py-3 rounded-xl"
          >
            Search
          </button>
        </div>
      </ModalV2>
      <ModalV2
        isOpen={joinCaseModal}
        isClose={() => setJoinCaseModal(false)}
        maxWidth="w-[442px]"
        edges="rounded-xl"
      >
        <div className="px-8 py-10 flex flex-col items-start w-full gap-y-5">
          <span className="text-2xl font-semibold">
            Select who you are joining as
          </span>
          <div className="flex flex-col w-full gap-y-3">
            {partyType.map((type) => (
              <div className="flex items-center gap-x-3">
                <div
                  onClick={() => dispatch(setJoinAs(type.value as JoinAsType))}
                  className="w-[18px] h-[18px] flex items-center justify-center rounded-full border border-[#23A863]"
                >
                  {selectedJoinedAs === type.value && (
                    <div className="w-[10px] h-[10px] rounded-full bg-[#23A863]" />
                  )}
                </div>
                <span className="text-[16px] font-medium">{type.label}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              setJoinCaseModal(false);
              setAppealNumberModal(true);
            }}
            className="bg-[#009444] w-full text-white px-10 py-3 rounded-xl"
          >
            Proceed
          </button>
        </div>
      </ModalV2>
    </div>
  );
};

export default CaseFolders;
